import React, { useState, useEffect } from "react";
import "./Header.css";
import { AiOutlineSetting } from "react-icons/ai";
import { BellOutlined, UserOutlined, DownOutlined } from "@ant-design/icons";
import { TbLogout } from "react-icons/tb";
import { Badge, Dropdown, Space, Avatar, message, Popover, Empty, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import {
  logout,
  getNotificationCount,
  getRecentNotifications,
  markNotificationRead,
} from "../../services/ApiService";
import { decryptObject } from "../GlobalFunction";
import formatRelativeTime from "../../utils/formatRelativeTime";
import { NOTIFICATIONS_READ_EVENT, notifyNotificationsRead } from "../../utils/notificationEvents";

// How often the bell polls for a fresh unread count. 15s keeps it feeling
// close to live without adding meaningful load -- see
// services/notification_service.py::get_notification_count's docstring
// (backend) for why this polls a dedicated cheap endpoint rather than
// getAllNotifications, which does a full unpaginated fetch.
const NOTIFICATION_POLL_INTERVAL_MS = 15000;

const Header = ({ title, user }) => {
  const [show, setshow] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const res = await getNotificationCount();
        if (res.responseCode === 200) {
          setUnreadCount(res.unread_count || 0);
        }
      } catch (error) {
        console.error("Failed to fetch notification count", error);
      }
    };
    fetchNotificationCount();
    const intervalId = setInterval(fetchNotificationCount, NOTIFICATION_POLL_INTERVAL_MS);

    // Real bug fix, 2026-10-07: this Header instance and the full
    // notifications page are separate components with no shared state
    // -- marking something read there previously only reached this
    // bell on its NEXT poll tick (up to 15s later), which looked like
    // "mark all as read" silently didn't work. Re-fetch immediately
    // whenever anything (including this same bell's own dropdown)
    // marks a notification read -- see utils/notificationEvents.js.
    window.addEventListener(NOTIFICATIONS_READ_EVENT, fetchNotificationCount);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener(NOTIFICATIONS_READ_EVENT, fetchNotificationCount);
    };
  }, []);

  const sessionData = decryptObject(sessionStorage.getItem("sessioninfo"));
  const name = user?.name || sessionData.username;
  const designation = user?.designation || sessionData.designation;
  const navigate = useNavigate();

  // Fetched only when the panel actually opens -- the always-running
  // poll above only ever asks for the count, not the list itself.
  const handlePanelOpenChange = async (open) => {
    setPanelOpen(open);
    if (!open) return;
    setLoadingRecent(true);
    try {
      const res = await getRecentNotifications();
      if (res.responseCode === 200) {
        setRecentNotifications(res.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch recent notifications", error);
    } finally {
      setLoadingRecent(false);
    }
  };

  const handleNotificationClick = async (item) => {
    // Opening a notification with a link_url (e.g. a ticket reply) should
    // take the user straight there, same "Go to ..." action the full
    // notifications page already has -- confirmed with the user,
    // 2026-11-02. Marking it read still happens either way; only skip
    // the mark-read call itself when it's already read.
    if (item.status === 1) {
      try {
        const res = await markNotificationRead(item.id);
        if (res.responseCode === 200) {
          setRecentNotifications((prev) =>
            prev.map((n) => (n.id === item.id ? { ...n, status: 0 } : n))
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
          notifyNotificationsRead();
        }
      } catch (error) {
        console.error("Failed to mark notification read", error);
      }
    }
    if (item.link_url) {
      setPanelOpen(false);
      navigate(item.link_url);
    }
  };

  const notificationPanel = (
    <div style={{ width: 340 }}>
      <div
        style={{
          padding: "0.75rem 1rem",
          borderBottom: "1px solid #EFF0F6",
          fontWeight: 600,
          fontSize: "0.9rem",
        }}
      >
        Notifications
      </div>
      <div style={{ maxHeight: 360, overflowY: "auto" }}>
        {loadingRecent ? (
          <div style={{ padding: "2rem 0", textAlign: "center" }}>
            <Spin size="small" />
          </div>
        ) : recentNotifications.length === 0 ? (
          <Empty
            description="No notifications yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "1.5rem 1rem" }}
          />
        ) : (
          recentNotifications.map((item) => (
            <div
              key={item.id}
              onClick={() => handleNotificationClick(item)}
              style={{
                padding: "0.7rem 1rem",
                cursor: item.status === 1 ? "pointer" : "default",
                backgroundColor: item.status === 1 ? "#F6F7FB" : "white",
                borderBottom: "1px solid #F5F5F5",
                position: "relative",
              }}
            >
              {item.status === 1 && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: "0.25rem",
                    backgroundColor: "#4A3AFF",
                  }}
                />
              )}
              <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                {item.title || "Notification"}
              </div>
              <div style={{ fontSize: "0.78rem", color: "#787486", marginTop: 2 }}>
                {item.message}
              </div>
              <div style={{ fontSize: "0.72rem", color: "#A0A0A0", marginTop: 4 }}>
                {formatRelativeTime(item.created_date)}
              </div>
            </div>
          ))
        )}
      </div>
      <div
        onClick={() => {
          setPanelOpen(false);
          navigate("/notification");
        }}
        style={{
          padding: "0.6rem 1rem",
          textAlign: "center",
          borderTop: "1px solid #EFF0F6",
          cursor: "pointer",
          color: "#4A3AFF",
          fontWeight: 600,
          fontSize: "0.82rem",
        }}
      >
        View all
      </div>
    </div>
  );

  let items = [
    {
      label: <span onClick={() => navigate("/settings")}>Profile</span>,
      key: "1",
      icon: <UserOutlined />,
    },
    {
      label: (
        <span
          onClick={() => {
            navigate("/login");
          }}
        >
          Logout
        </span>
      ),
      key: "4",
      icon: <TbLogout />,
    },
  ];

  const handleLogout = async () => {
    try {
      const res = await logout();
      if (res.responseCode === 200) {
        sessionStorage.clear();
        navigate("/login");
      } else {
        message.error(res.responseMessage);
      }
    } catch (error) {
      message.error(error);
    }
  };

  return (
    <>
      <div className="header-main">
        <div className="search">
          {!title ? (
            <></>
          ) : (
            <span style={{ fontWeight: "600", fontSize: "0.8rem" }}>
              {title}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/settings")}
          >
            <AiOutlineSetting
              style={{ color: "#787486", fontSize: "1.3rem" }}
            />
          </span>
          <Popover
            content={notificationPanel}
            trigger="click"
            open={panelOpen}
            onOpenChange={handlePanelOpenChange}
            placement="bottomRight"
            styles={{ body: { padding: 0 } }}
          >
            <Badge count={unreadCount} size="small" style={{ cursor: "pointer" }}>
              <BellOutlined
                style={{
                  fontSize: "1.3rem",
                  color: "#787486",
                  cursor: "pointer",
                }}
              />
            </Badge>
          </Popover>
          <Dropdown
            menu={{
              items,
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Space>
                <div className="user-details">
                  <span className="name">{name}</span>
                  <span className="designation">{designation}</span>
                </div>
                <Avatar size="small" icon={<UserOutlined />} />
              </Space>
              <DownOutlined style={{ marginLeft: "10px", cursor: "pointer" }} />
            </div>
          </Dropdown>
          <div onClick={handleLogout} className="logout-icon">
            <TbLogout
              style={{
                color: "white",
                fontSize: "1.1rem",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
